/**
 * EmailStartScreen.jsx — s95d, the one front door (Dr. Rami chose "email first").
 *
 * Welcome's "Continue with email" lands here. The patient types an email; after the
 * Cloudflare check, POST /auth/email-check says whether a live account holds it and how it
 * signs in. Then:
 *   · a password account  -> /signin with the email filled in ("Welcome back");
 *   · an Apple/Google-only account -> the matching button, here;
 *   · no account -> /signup with the email filled in (the existing form, consent and all).
 * The email travels in router state, never in the URL.
 */
import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useI18n } from '../../i18n'
import { authAPI } from '../../services/api'
import Turnstile from '../../components/Turnstile'
import useSsoSignIn from '../../hooks/useSsoSignIn'
import SsoCodeSheet from '../../components/SsoCodeSheet'
import { AppleLogo, GoogleLogo } from '../../components/SsoLogos'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import orellaWordmarkBlue from '../../assets/logos/orella-wordmark-blue-transparent.png'

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const looksLikeEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((v || '').trim())

export default function EmailStartScreen() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const sso = useSsoSignIn({ mode: 'signin' })
  const [email, setEmail] = useState(location.state?.email || '')
  const [token, setToken] = useState(null)
  const [widgetKey, setWidgetKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [ssoOnly, setSsoOnly] = useState(null) // ['google'] | ['apple'] | both

  const go = async (e) => {
    e.preventDefault()
    if (busy || !looksLikeEmail(email)) return
    if (!token) { setError(t('auth.verifyHuman', 'Please complete the verification below.')); return }
    setBusy(true); setError(null); setSsoOnly(null)
    const clean = email.trim().toLowerCase()
    try {
      const r = await authAPI.emailCheck(clean, token)
      if (!r?.exists) {
        navigate('/signup', { state: { email: clean, fromFrontDoor: true } })
      } else if ((r.methods || []).includes('password')) {
        navigate('/signin', { state: { email: clean, fromFrontDoor: true } })
      } else {
        setSsoOnly(r.methods || [])
      }
    } catch (err) {
      setError(err?.error || t('emailStart.failed', 'We could not check that email. Please try again.'))
    } finally {
      // Turnstile tokens are single-use: a second try gets a fresh challenge.
      setToken(null); setWidgetKey((k) => k + 1)
      setBusy(false)
    }
  }

  const field = {
    width: '100%', height: 48, borderRadius: 12, border: '1.5px solid #CBD5E1', padding: '0 14px',
    fontSize: 16, color: NAVY, background: '#fff',
  }

  return (
    <ResponsiveWrapper bottomInset={false}>
      <div className="screen-min-h bg-white flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="px-5 pt-3">
          <button type="button" onClick={() => navigate('/welcome')}
            className="rounded-full flex items-center justify-center"
            style={{ width: 36, height: 36, background: '#fff', border: '1px solid #E2E8F0' }}
            aria-label={t('common.back', 'Back')}>
            <ChevronLeft size={18} style={{ color: NAVY }} />
          </button>
        </div>
        <form onSubmit={go} className="flex-1 flex flex-col px-6" style={{ paddingTop: 18 }}>
          <img src={orellaWordmarkBlue} alt="Orella" style={{ height: 30, width: 'auto', alignSelf: 'flex-start', marginBottom: 18 }} />
          <h1 style={{ fontSize: 24, fontWeight: 700, color: NAVY, margin: '0 0 6px', letterSpacing: '-0.01em' }}>
            {t('emailStart.title', 'Continue with email')}
          </h1>
          <p style={{ fontSize: 14.5, color: '#5B6E80', margin: '0 0 18px' }}>
            {t('emailStart.body', "We'll sign you in, or help you create an account.")}
          </p>
          <label htmlFor="front-email" style={{ fontSize: 13, fontWeight: 600, color: NAVY, marginBottom: 6 }}>{t('auth.email')}</label>
          <input id="front-email" type="email" autoComplete="email" inputMode="email" autoFocus
            value={email} onChange={(e) => { setEmail(e.target.value); setError(null); setSsoOnly(null) }}
            placeholder={t('auth.emailPlaceholder')} style={field} />
          <div className="flex justify-center" style={{ minHeight: 65, marginTop: 14 }}>
            <Turnstile key={widgetKey} onVerify={setToken} onExpire={() => setToken(null)} onError={() => setToken(null)} />
          </div>
          {error && <p role="alert" style={{ fontSize: 13, color: '#DC2626', marginTop: 8 }}>{error}</p>}
          {ssoOnly && (
            <div role="status" className="rounded-2xl" style={{ background: '#EFF8FF', border: '1px solid #BFE1FB', padding: 14, marginTop: 10 }}>
              <p style={{ fontSize: 14, color: NAVY, margin: '0 0 10px' }}>
                {ssoOnly.includes('apple') && !ssoOnly.includes('google')
                  ? t('emailStart.usesApple', 'This email signs in with Apple.')
                  : t('emailStart.usesGoogle', 'This email signs in with Google.')}
              </p>
              {ssoOnly.includes('apple') && (
                <button type="button" onClick={sso.startApple} disabled={!!sso.ssoLoading}
                  className="w-full flex items-center justify-center gap-2.5 font-semibold rounded-full"
                  style={{ height: 48, background: '#000', color: '#fff', fontSize: 15, marginBottom: 8 }}>
                  <AppleLogo /> {t('signIn.continueWithApple')}
                </button>
              )}
              {ssoOnly.includes('google') && (
                <button type="button" onClick={sso.startGoogle} disabled={!!sso.ssoLoading}
                  className="w-full flex items-center justify-center gap-2.5 font-semibold rounded-full"
                  style={{ height: 48, background: '#fff', color: NAVY, border: '1.5px solid #E2E8F0', fontSize: 15 }}>
                  <GoogleLogo /> {t('signIn.continueWithGoogle')}
                </button>
              )}
              {sso.ssoError && <p role="alert" style={{ fontSize: 13, color: '#DC2626', marginTop: 8 }}>{sso.ssoError}</p>}
            </div>
          )}
          {/* PROD-1176: no flex:1 spacer (it pinned Continue to the bottom of a 100vh column, under
              Safari's toolbar). PROD-1194: Continue is in the pinned bar, which sticks to the bottom
              of the VISIBLE scroller and pays the inset itself (ResponsiveWrapper bottomInset off). */}
          <BottomActionBar className="-mx-6 [--bar-px:24px]">
            <button type="submit" disabled={busy || !looksLikeEmail(email) || !token}
              className="w-full font-bold text-white rounded-full active:scale-[0.98] transition-transform"
              style={{ height: 50, background: busy || !looksLikeEmail(email) || !token ? '#CBD5E1' : BLUE, fontSize: 16 }}>
              {busy ? t('emailStart.checking', 'Checking…') : t('common.continue', 'Continue')}
            </button>
          </BottomActionBar>
        </form>
      </div>
      <SsoCodeSheet step={sso.codeStep} busy={sso.codeBusy} error={sso.codeError} onSubmit={sso.submitCode} onCancel={sso.cancelCode} />
    </ResponsiveWrapper>
  )
}
